import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Seo } from '@/components/Seo';
import { signInSchema, signUpSchema } from '@/lib/auth-validation';
import SurfLevelSelector from '@/components/dashboard/SurfLevelSelector';
import { surfLevelToDbEnum } from '@/hooks/useSurfProgression';
import type { SurfLevel } from '@/lib/surf-progression';

// My Swell Breaks is the signed-in user's home (Surf Passport, Saved Trips, and
// future personal features) -- the default landing page for a normal sign-in or
// sign-up with no more specific intended destination.
const DEFAULT_AUTHENTICATED_ROUTE = '/my-swell-breaks';

// State shape other pages hand this route via `navigate('/auth', { state })`.
// `from` is the established pattern (see MyPassport.tsx/MyTrips.tsx);
// `initialTab` and `plannerHandoff` extend it for the Trip Planner's
// results-screen account CTA (handleAccountCtaClick in
// TripPlannerWizard.tsx), which needs to land a signed-out visitor on the
// sign-up tab and hand its completed preferences/results back once
// authentication succeeds. `plannerHandoff`'s contents are opaque here --
// this page only relays it, it doesn't need to know its shape.
interface AuthLocationState {
  from?: string;
  initialTab?: 'signin' | 'signup';
  plannerHandoff?: unknown;
}

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const locationState = location.state as AuthLocationState | null;
  const [tab, setTab] = useState<'signin' | 'signup'>(locationState?.initialTab ?? 'signin');
  const [submitting, setSubmitting] = useState(false);

  // Sign in
  const [siEmail, setSiEmail] = useState('');
  const [siPassword, setSiPassword] = useState('');

  // Sign up
  const [suName, setSuName] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suPassword, setSuPassword] = useState('');
  const [suLevel, setSuLevel] = useState<SurfLevel | null>(null);

  // A protected page (e.g. MyPassport.tsx) that redirected a signed-out visitor
  // here passes its own path as `from`, so authenticating returns them to what
  // they actually asked for instead of the generic default.
  const from = locationState?.from;
  const destinationAfterAuth = from || DEFAULT_AUTHENTICATED_ROUTE;
  const plannerHandoff = locationState?.plannerHandoff;

  // The SOLE post-auth redirect path -- handleSignIn/handleSignUp below
  // deliberately do not call navigate() themselves on success. Supabase's
  // auth-js fires its onAuthStateChange listener (which is what updates
  // `user` here, via AuthContext) synchronously inside signInWithPassword()/
  // signUp()'s own body, *before* their returned promise resolves back into
  // the handler that awaited it. A handler-side navigate() after that await
  // would therefore race this effect: both could fire, producing a second,
  // redundant history entry (still carrying the original plannerHandoff,
  // since it's a separate navigate() call with its own closure) on top of
  // the one this effect already replaced -- confirmed against the installed
  // @supabase/auth-js source. Reacting to `user`/`loading` here instead
  // means there is exactly one place that ever navigates after a successful
  // authentication, however that authentication resolved (a fresh sign-in,
  // a fresh sign-up, or -- unchanged from before -- an already-authenticated
  // visitor landing on this page at all).
  useEffect(() => {
    if (!loading && user) {
      navigate(destinationAfterAuth, {
        replace: true,
        ...(plannerHandoff ? { state: { plannerHandoff } } : {}),
      });
    }
  }, [user, loading, navigate, destinationAfterAuth, plannerHandoff]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = signInSchema.safeParse({ email: siEmail, password: siPassword });
    if (!parsed.success) {
      toast({ title: 'Check your details', description: parsed.error.issues[0].message, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: parsed.data.email,
      password: parsed.data.password,
    });
    setSubmitting(false);
    if (error) {
      // Deliberately generic and identical for every failure reason
      // (invalid credentials, unconfirmed email, etc.) — branching the copy
      // on error.code would let someone confirm a specific account exists
      // and is pending confirmation. Real error still logged for
      // debugging.
      console.error('Sign in failed:', error);
      toast({
        title: 'Sign in failed',
        description: 'Check your email and password and try again.',
        variant: 'destructive',
      });
      return;
    }
    toast({ title: 'Welcome back', description: 'Signed in successfully.' });
    // No navigate() here -- the effect above is the sole redirect path (see
    // its own comment for why); `user` updating is what triggers it.
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = signUpSchema.safeParse({ fullName: suName, email: suEmail, password: suPassword });
    if (!parsed.success) {
      toast({ title: 'Check your details', description: parsed.error.issues[0].message, variant: 'destructive' });
      return;
    }
    if (!suLevel) {
      toast({ title: 'One more thing', description: 'Let us know your surfing level to continue.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    // full_name and surf_level both travel via raw_user_meta_data -- a
    // database trigger (handle_new_user, AFTER INSERT ON auth.users) reads
    // them from there and creates the user_profiles row transactionally, as
    // part of the same insert that creates the auth user itself. This is why
    // there's no separate client-side profile write here anymore: the row
    // already exists by the time signUp()'s promise resolves, whether or not
    // email confirmation returned a session. See the trigger's own migration
    // comment for why it's the sole, authoritative path (not a second
    // competing one) -- it never reads or writes `role`, and an invalid
    // surf_level value is defensively dropped there rather than raised, so it
    // can never abort signup.
    const { data, error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { full_name: parsed.data.fullName, surf_level: surfLevelToDbEnum(suLevel) },
      },
    });
    setSubmitting(false);
    if (error) {
      // Deliberately generic, same reasoning as handleSignIn above: Supabase's
      // signup error for an already-registered email is distinguishable from
      // other failure reasons, and passing error.message straight through
      // would let someone confirm whether a given email already has an
      // account. Real error still logged for debugging.
      console.error('Sign up failed:', error);
      toast({
        title: 'Sign up failed',
        description:
          "We couldn't create your account. Double-check your details and try again — if you already have an account, try signing in instead.",
        variant: 'destructive',
      });
      return;
    }

    if (!data.session) {
      toast({
        title: 'Check your inbox',
        description: 'Confirm your email to finish signing up.',
      });
    } else {
      toast({ title: 'Account created', description: 'Welcome to Swell Breaks.' });
      // No navigate() here -- the effect above is the sole redirect path
      // (see its own comment for why); `user` updating is what triggers it.
      // The no-session branch above (email confirmation required) never
      // reaches this point, so the effect only ever fires once this user is
      // genuinely authenticated.
    }
  };

  return (
    <Layout>
      <Seo title="Sign in | Swell Breaks" description="Sign in or create your Swell Breaks account." path="/auth" noIndex />
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Welcome to Swell Breaks
            </h1>
            <p className="text-muted-foreground text-sm">
              Save destinations, track your trips, and unlock the full archive.
            </p>
          </div>

          <Card className="border-border/50 bg-card border-foreground/20">
            <Tabs value={tab} onValueChange={(v) => setTab(v as 'signin' | 'signup')}>
              <CardHeader className="pb-2">
                <TabsList className="grid grid-cols-2 w-full text-foreground">
                  <TabsTrigger value="signin" className="data-[state=inactive]:text-foreground/90">Sign In</TabsTrigger>
                  <TabsTrigger value="signup" className="data-[state=inactive]:text-foreground/90">Create Account</TabsTrigger>
                </TabsList>

              </CardHeader>

              <CardContent className="pt-4">
                <TabsContent value="signin" className="mt-0">
                  <CardTitle className="font-serif text-xl mb-1">Sign in</CardTitle>
                  <CardDescription className="mb-4">Welcome back. Pick up where you left off.</CardDescription>
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="si-email">Email</Label>
                      <Input id="si-email" type="email" autoComplete="email" value={siEmail} onChange={(e) => setSiEmail(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="si-password">Password</Label>
                        <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground underline">
                          Forgot password?
                        </Link>
                      </div>
                      <Input id="si-password" type="password" autoComplete="current-password" value={siPassword} onChange={(e) => setSiPassword(e.target.value)} required />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Sign In
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup" className="mt-0">
                  <CardTitle className="font-serif text-xl mb-1">Create your account</CardTitle>
                  <CardDescription className="mb-4">It only takes a moment.</CardDescription>
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="su-name">Full name</Label>
                      <Input id="su-name" value={suName} onChange={(e) => setSuName(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="su-email">Email</Label>
                      <Input id="su-email" type="email" autoComplete="email" value={suEmail} onChange={(e) => setSuEmail(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="su-password">Password</Label>
                      <Input id="su-password" type="password" autoComplete="new-password" value={suPassword} onChange={(e) => setSuPassword(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Your surfing level</Label>
                      <SurfLevelSelector value={suLevel} onSelect={setSuLevel} />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Create Account
                    </Button>
                  </form>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            By continuing you agree to our{' '}
            <Link to="/terms-and-conditions" className="underline hover:text-foreground">Terms</Link> and{' '}
            <Link to="/privacy-policy" className="underline hover:text-foreground">Privacy Policy</Link>.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Auth;
